/**
 * Monte Carlo uncertainty analysis for SAFECARE simulator.
 * Samples key uncertain parameters, runs multiple scenarios, and computes
 * percentile bands (e.g. 5th, 50th, 95th) for key outputs.
 * All runs and bands are documented for telemetry export.
 */

import { runScenario } from './runScenario.js';

/** Default number of runs for in-browser (balance speed vs stability of percentiles) */
const DEFAULT_RUNS = 50;

/** Percentiles to compute (for charts and export) */
const PERCENTILES = [5, 25, 50, 75, 95];

/**
 * Parameter definitions for uncertainty: key, min, max (in same units as config).
 * Values are sampled uniformly in [min, max] unless a distribution is specified later.
 */
const UNCERTAINTY_PARAMS = [
    { key: 'taxEvasionRate', min: 0.08, max: 0.16 },
    { key: 'utilizationSurge', min: 0.10, max: 0.22 },
    { key: 'adminEfficiencyFloor', min: 0.03, max: 0.06 },
    { key: 'internationalPricePctOfUS', min: 0.35, max: 0.50 },
];

/**
 * Sample one value uniformly in [min, max].
 */
function sampleUniform(min, max) {
    return min + Math.random() * (max - min);
}

/**
 * Create a perturbed config from base: only uncertain keys are overwritten with sampled values.
 */
function sampleConfig(baseConfig) {
    const config = { ...baseConfig };
    for (const { key, min, max } of UNCERTAINTY_PARAMS) {
        if (config[key] !== undefined) {
            config[key] = sampleUniform(min, max);
        }
    }
    return config;
}

/**
 * Extract key yearly series from a single run as a map year -> values (for alignment across runs).
 */
function extractYearlyMap(yearlyResults) {
    if (!yearlyResults || !yearlyResults.length) return null;
    const map = {};
    yearlyResults.forEach(y => {
        map[y.year] = {
            totalRevenue: y.totalRevenue ?? 0,
            totalSpending: y.totalSpending ?? 0,
            surplus: (y.totalRevenue ?? 0) - (y.totalSpending ?? 0),
            safecareHealthShareGDP: y.safecareHealthShareGDP ?? 0,
            totalSavings: y.totalSavings ?? 0,
        };
    });
    return map;
}

/**
 * Extract trust fund balance by year from run result (map year -> balance).
 * Trust fund yearlyData uses adjustedEndingBalance (fiscalCalculator), not balance.
 */
function extractTrustFundMap(trustFund) {
    const data = trustFund?.yearlyData;
    if (!data || !data.length) return {};
    const map = {};
    data.forEach(d => {
        const bal = d.adjustedEndingBalance ?? d.balance ?? d.endingBalance ?? 0;
        map[d.year] = bal;
    });
    return map;
}

/**
 * Compute percentile of a sorted array (linear interpolation).
 */
function percentile(sortedArr, p) {
    if (!sortedArr.length) return null;
    const idx = (p / 100) * (sortedArr.length - 1);
    const lo = Math.floor(idx);
    const hi = Math.ceil(idx);
    if (lo === hi) return sortedArr[lo];
    return sortedArr[lo] + (idx - lo) * (sortedArr[hi] - sortedArr[lo]);
}

/**
 * Run Monte Carlo analysis: N runs with sampled parameters, aggregate percentiles.
 * @param {Object} baseConfig - Base policy config (same as main run)
 * @param {Object} options - { runs?: number, enabledEventIds?: array }
 * @returns {Object} { percentilesByYear, summaryPercentiles, runCount, sampledKeys, runMeta }
 */
export function runMonteCarlo(baseConfig, options = {}) {
    const runs = options.runs ?? DEFAULT_RUNS;
    const enabledEventIds = options.enabledEventIds ?? [];

    const surplusByYear = {};
    const revenueByYear = {};
    const spendingByYear = {};
    const healthShareByYear = {};
    const trustFundByYear = {};
    const averageSurplusRuns = [];
    const finalTrustFundRuns = [];

    for (let i = 0; i < runs; i++) {
        const config = sampleConfig(baseConfig);
        const result = runScenario(config, enabledEventIds);
        const scenario = result.scenarioResults || result.yearlyResults || [];
        const tf = result.trustFund;

        const yearMap = extractYearlyMap(scenario);
        if (yearMap) {
            Object.keys(yearMap).forEach(year => {
                const y = Number(year);
                const v = yearMap[year];
                if (!surplusByYear[y]) surplusByYear[y] = [];
                if (!revenueByYear[y]) revenueByYear[y] = [];
                if (!spendingByYear[y]) spendingByYear[y] = [];
                if (!healthShareByYear[y]) healthShareByYear[y] = [];
                surplusByYear[y].push(v.surplus);
                revenueByYear[y].push(v.totalRevenue);
                spendingByYear[y].push(v.totalSpending);
                healthShareByYear[y].push(v.safecareHealthShareGDP);
            });
        }

        const tfMap = extractTrustFundMap(tf);
        Object.keys(tfMap).forEach(year => {
            const y = Number(year);
            if (!trustFundByYear[y]) trustFundByYear[y] = [];
            trustFundByYear[y].push(tfMap[year]);
        });
        const tfYears = Object.keys(tfMap).map(Number).sort((a, b) => a - b);
        if (tfYears.length) finalTrustFundRuns.push(tfMap[tfYears[tfYears.length - 1]]);

        if (result.summary?.averageSurplus != null) {
            averageSurplusRuns.push(result.summary.averageSurplus);
        }
    }

    const years = Array.from(new Set([
        ...Object.keys(surplusByYear).map(Number),
        ...Object.keys(trustFundByYear).map(Number)
    ])).sort((a, b) => a - b);
    const percentilesByYear = {};

    const sortNum = (a, b) => a - b;
    years.forEach(year => {
        const surplusAtYear = surplusByYear[year] || [];
        const revenueAtYear = revenueByYear[year] || [];
        const spendingAtYear = spendingByYear[year] || [];
        const healthShareAtYear = healthShareByYear[year] || [];
        const tfAtYear = trustFundByYear[year] || [];

        percentilesByYear[year] = {
            surplus: PERCENTILES.reduce((acc, p) => {
                acc[p] = surplusAtYear.length ? percentile([...surplusAtYear].sort(sortNum), p) : null;
                return acc;
            }, {}),
            totalRevenue: PERCENTILES.reduce((acc, p) => {
                acc[p] = revenueAtYear.length ? percentile([...revenueAtYear].sort(sortNum), p) : null;
                return acc;
            }, {}),
            totalSpending: PERCENTILES.reduce((acc, p) => {
                acc[p] = spendingAtYear.length ? percentile([...spendingAtYear].sort(sortNum), p) : null;
                return acc;
            }, {}),
            safecareHealthShareGDP: PERCENTILES.reduce((acc, p) => {
                acc[p] = healthShareAtYear.length ? percentile([...healthShareAtYear].sort(sortNum), p) : null;
                return acc;
            }, {}),
            trustFundBalance: PERCENTILES.reduce((acc, p) => {
                acc[p] = tfAtYear.length ? percentile([...tfAtYear].sort(sortNum), p) : null;
                return acc;
            }, {}),
        };
    });

    const summaryPercentiles = {
        averageSurplus: PERCENTILES.reduce((acc, p) => {
            acc[p] = percentile([...averageSurplusRuns].sort((a, b) => a - b), p);
            return acc;
        }, {}),
        finalTrustFundBalance: PERCENTILES.reduce((acc, p) => {
            acc[p] = percentile([...finalTrustFundRuns].sort((a, b) => a - b), p);
            return acc;
        }, {}),
    };

    return {
        percentilesByYear,
        summaryPercentiles,
        runCount: runs,
        sampledKeys: UNCERTAINTY_PARAMS.map(p => p.key),
        percentileLevels: PERCENTILES,
        paramRanges: UNCERTAINTY_PARAMS.map(({ key, min, max }) => ({ key, min, max })),
        runMeta: {
            timestamp: new Date().toISOString(),
            enabledEventIds: Array.isArray(enabledEventIds) ? enabledEventIds : Object.keys(enabledEventIds).filter(k => enabledEventIds[k]?.enabled),
        },
    };
}
